import { useCallback, useEffect, useRef, useState } from 'react'
import {
  X, Loader2, Trash2, Plus, UserRound, Camera, BadgeDollarSign, StickyNote, FileText, Lock, MailCheck,
} from 'lucide-react'
import { apiGet, cachedGet, postJson, patchJson, invalidateCache } from '../../lib/api.js'
import {
  PREFIXES, ASSIGNABLE_ROLES, LOCKED_ROLES, BANKS, relabelBranch, toDateInput, fmtDateTH,
  digitsOnly, employeeCode, PHONE_LEN, IDCARD_LEN, positionChoices, positionKey,
} from '../../lib/employee.js'
import SearchableSelect from '../SearchableSelect.jsx'
import LockedInput from '../LockedInput.jsx'
import Combobox from '../Combobox.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'
import ContractDialog from './ContractDialog.jsx'

const FIELD_BASE = 'w-full rounded-xl border bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const FIELD = `${FIELD_BASE} border-slate-200`
// The same control while it is the thing standing between you and บันทึก. Built from
// FIELD_BASE rather than appended to FIELD: two `border-*` utilities in one class
// string are resolved by stylesheet order, not by which one you typed last.
const FIELD_WARN = `${FIELD_BASE} border-amber-300 ring-4 ring-amber-500/10`
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'


const PREFIX_OPTS = PREFIXES.map((p) => ({ value: p, label: p }))
const BANK_BLANK = { value: '', label: '— เลือกธนาคาร —' }

// The stored bank is kept even when it is not in BANKS. `Employee.bank` is an enum,
// so an out-of-catalog value should be impossible — but the catalog has been renamed
// before (ทหารไทย → ทหารไทยธนชาต, as lib/dentist.js records for the doctor table), and
// during a rename the old value is still on the rows. Without this the picker renders
// blank and the next save silently wipes a bank account off a payroll record.
function bankOptions(current) {
  const names = current && !BANKS.includes(current) ? [current, ...BANKS] : BANKS
  return [BANK_BLANK, ...names.map((b) => ({ value: b, label: b }))]
}
// The two entries payroll reads by name (lib/employee.js salaryEntry) — anything
// else is a free-text one-off, which is why the picker allows creating.
const SALARY_NAMES = ['Base Salary', 'Position Fee']

// Why each locked role cannot be toggled here — shown on hover of the locked chip.
const LOCKED_ROLE_HINT = {
  Admin: 'บทบาท Admin เปลี่ยนที่หน้านี้ไม่ได้ — เปิดสิทธิ์ทั้งระบบ ต้องแก้โดยผู้ดูแลระบบโดยตรง',
  Doctor: 'บทบาท Doctor เปลี่ยนที่หน้านี้ไม่ได้ — ข้อมูลทันตแพทย์อยู่ในระบบหมอ ไม่ได้ตั้งที่ประวัติพนักงาน',
}

const EMPTY = {
  prefix: 'น.ส.', name: '', nickname: '', name_eng: '', email: '', phone: '', address: '',
  idcardnumber: '', code: '', position: '', role: [], branch_id: 0, branch_acc_id: 0,
  bank: '', bank_branchcode: '', bank_num: '', active: true, sso: false, leavequota: 0,
  date_start: '', date_probationpass: '', date_end: '',
}

function Field({ label, hint, required, aside, children }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <label className={LABEL}>{label}{required && <span className="text-rose-500"> *</span>}</label>
        {aside}
      </div>
      {children}
      {hint && <p className="text-[11.5px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  )
}

// "7/10" beside a fixed-length label — the count is the only thing that says why
// บันทึก is about to refuse, and it says it while the digits are still being typed.
function Counter({ value, len }) {
  const n = value.length
  if (!n) return null
  return (
    <span className={`text-[11.5px] tabular-nums mb-1 ${n === len ? 'text-emerald-600' : 'text-rose-500'}`}>
      {n}/{len}
    </span>
  )
}

function SectionHead({ icon: Icon, title, note }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2.5">
      <Icon size={15} className="text-brand-600" strokeWidth={1.9} />
      <h3 className="text-[13.5px] font-semibold text-slate-700">{title}</h3>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
    </div>
  )
}

// A real switch, not a pill. The previous version was a coloured chip with a dot,
// which reads as a status BADGE — "ไม่ส่ง สปส." looked like a recorded value rather
// than something you could click to change.
function Toggle({ checked, onChange, labelOn, labelOff }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="group inline-flex items-center gap-2.5 py-2 text-[13px] font-medium"
    >
      <span
        className={`relative h-5 w-9 shrink-0 rounded-full transition-colors group-focus-visible:ring-4 group-focus-visible:ring-brand-500/10 ${
          checked ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </span>
      <span className={checked ? 'text-emerald-700' : 'text-slate-500'}>{checked ? labelOn : labelOff}</span>
    </button>
  )
}

/* ── the profile photo upload ───────────────────────────────────────────────
 * 🔑 WHY THIS IS TWO SHAPES AND NOT ONE `fetch`. Posting a photo as plain
 * multipart makes the Worker run `request.formData()`, which materialises the
 * whole file inside the 128 MB isolate that EVERY /api route shares — the
 * buffering that answered Cloudflare Error 1102 to unrelated page loads on
 * 2026-08-27/28 (functions/lib/uploadGuard.js has the measurements). Sending the
 * body in the field shape Xano itself takes lets the Worker pipe it through
 * unread instead, and hold none of it.
 *
 * This route cannot use lib/uploadDirect.js: `uploadFile()` presigns a PUT at a
 * Wasabi object, and an employee photo does not go to Wasabi — it goes to Xano,
 * which stores its own public copy and deletes the previous one in the same call.
 * functions/api/hr/employee-image.js explains that at length. So the fallback
 * ladder is reproduced here, deliberately small, rather than bent into a helper
 * whose whole contract is "sign a Wasabi key".
 */

// An endpoint whose streamed shape we have already seen fail. Remembered for the
// tab's lifetime so the wasted upload is paid once, not once per photo — the same
// bargain `directBroken` makes in src/lib/uploadDirect.js, and cleared the same
// way, by a reload, which is what you want after the cause is fixed.
let streamBroken = false

/**
 * One POST to /api/hr/employee-image.
 *
 * `streamed` builds the body in Xano's own field shape ({ content, employee_id })
 * and asks the Worker to pipe it through; the legacy shape ({ file, employee_id })
 * is the one the Worker unpacks and re-wraps. `employee_id` rides in the query
 * string too on the streamed call — a piped body is a body nobody upstream can
 * read, so that copy is what lets the route fail fast on a missing id.
 */
async function postProfileImage(id, file, streamed) {
  const fd = new FormData()
  fd.append(streamed ? 'content' : 'file', file)
  fd.append('employee_id', String(id))
  const url = streamed
    ? `/api/hr/employee-image?stream=1&employee_id=${encodeURIComponent(id)}`
    : '/api/hr/employee-image'
  const r = await fetch(url, { method: 'POST', body: fd, credentials: 'include' })
  if (!r.ok) {
    const e = new Error(`อัปโหลดรูปไม่สำเร็จ (${r.status})`)
    // A refusal is the ANSWER, not a transport problem — retrying it the slow way
    // would only buffer a file we already know is going to be rejected.
    //
    // ⚠️ 400 is NOT on that list, unlike `isRefusal` in lib/uploadDirect.js, and
    // the difference is the point: a Xano multipart parse that chokes on a piped
    // body is exactly what a 400 looks like here. Treating it as final would mean
    // the streamed path could never fall back to the one that works.
    e.refused = r.status === 401 || r.status === 403 || r.status === 413 || r.status === 415
    throw e
  }
  return r.json().catch(() => ({}))
}

export default function EmployeeEditor({ mode, employeeId, branches, canSalary, isHrAdmin, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const [form, setForm] = useState(EMPTY)
  const [salary, setSalary] = useState([])
  const [remark, setRemark] = useState([])
  const [imageUrl, setImageUrl] = useState('')
  const [pendingImage, setPendingImage] = useState(null) // { file, preview } — create-mode, uploaded after save
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState('')
  const [contractOpen, setContractOpen] = useState(false)
  // ตำแหน่ง is a catalog now (employee_position), not a hardcoded enum. `null` means
  // "not loaded yet / the request failed" — positionChoices() falls back to the
  // built-in list for that, so the field is never dead while the fetch is in flight.
  const [positions, setPositions] = useState(null)
  // The address as STORED. Locking is keyed on this, not on form.email: a row that
  // has no address has no login account to protect and cannot satisfy the required
  // field on Xano's PATCH either, so that one stays editable until it has one.
  const [storedEmail, setStoredEmail] = useState('')
  // A create that succeeded while its claim email did NOT. The employee exists, so
  // the dialog must not close on it — nobody would ever learn the mail failed.
  const [createdId, setCreatedId] = useState(0)
  // Set when a create's activation email failed, and cleared/replaced by the resend
  // below. The mail is not load-bearing — ลืมรหัสผ่าน on the login page is how an
  // employee (re)claims their own account either way — but its link dies in five
  // minutes, so the one HR sends on day one is usually dead before the hire opens it.
  const [claimNote, setClaimNote] = useState('')
  // ส่งอีเมลตั้งรหัสผ่านอีกครั้ง — edit mode only, and only once there is a stored address.
  // The endpoint reads the destination off the record and ignores anything sent to it,
  // so this mails exactly what ลืมรหัสผ่าน would mail to the same inbox.
  const [resending, setResending] = useState(false)
  const [askingResend, setAskingResend] = useState(false)
  const [resendErr, setResendErr] = useState('')
  const [resendNote, setResendNote] = useState('')
  // The เงินเดือน / โน้ต rows are typed here but written by their OWN endpoints the
  // moment ＋ is pressed — บันทึก never carried them. Holding the half-typed row in
  // the parent is what lets save() commit it too: a row filled in on screen and
  // then dropped on save is indistinguishable, to the person typing it, from the
  // save having failed. Reported 2026-08-19 against a Base Salary that vanished.
  const [salaryDraft, setSalaryDraft] = useState({ name: 'Base Salary', amount: '', remark: '' })
  const [noteDraft, setNoteDraft] = useState('')
  const [newPosition, setNewPosition] = useState('')   // pending create, awaiting confirm
  const [addingPosition, setAddingPosition] = useState(false)
  const fileRef = useRef(null)

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }))
  // Digit-only fields. Stripping on INPUT (not on save) is what makes the length
  // counter next to the label honest — "10/10" always means ten stored digits.
  const setDigits = (k, max) => (e) => {
    const v = digitsOnly(e.target.value).slice(0, max)
    setForm((f) => ({ ...f, [k]: v }))
  }

  // รหัสพนักงาน is derived, never typed — see employeeCode(). `form.code` still
  // holds what is on the record, so an older row can show what saving will change.
  const derivedCode = employeeCode(form.nickname, form.name)

  // Locked once there IS an address: you cannot CHANGE a login identity from here,
  // but you can fill in a missing one. The PATCH proxy enforces the same rule with
  // the stored value, so this is the affordance, not the guarantee.
  const emailLocked = isEdit && !!storedEmail

  // สนามเป้า is closed — relabelBranch renders it "อารีย์ (เดิมสนามเป้า)" and its staff
  // moved to อารีย์, so nobody new is posted there and it is out of the picker.
  // It reappears ONLY for a record that already sits on it: dropping the current
  // value from the options renders the select blank, and the next save would write
  // that blank over a real branch. Same reason bankOptions keeps an unlisted bank.
  const branchOptsFor = useCallback(
    (current) => {
      const open = (branches || []).filter(
        (b) => !/สนามเป้า/.test(b.name || '') || Number(b.id) === Number(current),
      )
      return [{ value: 0, label: '— เลือกสาขา —' }, ...open.map((b) => ({ value: Number(b.id), label: relabelBranch(b.name) }))]
    },
    [branches],
  )

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function hydrate(e) {
    setForm({
      prefix: e.prefix || 'น.ส.', name: e.name || '', nickname: e.nickname || '', name_eng: e.name_eng || '',
      email: e.email || '', phone: e.phone || '', address: e.address || '', idcardnumber: e.idcardnumber || '',
      code: e.code || '', position: e.position || '', role: Array.isArray(e.role) ? e.role : (e.role ? [e.role] : []),
      branch_id: Number(e.branch_id || 0), branch_acc_id: Number(e.branch_acc_id || 0),
      bank: e.bank || '', bank_branchcode: e.bank_branchcode || '', bank_num: e.bank_num || '',
      active: e.active !== false, sso: e.sso === true, leavequota: Number(e.leavequota || 0),
      date_start: toDateInput(e.date_start), date_probationpass: toDateInput(e.date_probationpass), date_end: toDateInput(e.date_end),
    })
    setSalary(Array.isArray(e.salary) ? e.salary : [])
    setRemark(Array.isArray(e.remark) ? e.remark : [])
    setImageUrl(e.image || '')
    setStoredEmail(String(e.email || '').trim())
  }

  useEffect(() => {
    if (!isEdit || !employeeId) return
    let alive = true
    setLoading(true)
    apiGet(`/api/hr/employees/${employeeId}`)
      .then((e) => { if (alive && e) hydrate(e) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [isEdit, employeeId])

  // The ตำแหน่ง catalog. Loaded once per open, for create and edit alike — a
  // failure is deliberately NOT surfaced as a form error: positionChoices() falls
  // back to the twelve built-in names, so the worst case is HR editing an employee
  // with yesterday's list rather than an editor that will not open.
  useEffect(() => {
    let alive = true
    cachedGet('/api/hr/positions')
      .then((rows) => { if (alive) setPositions(Array.isArray(rows) ? rows : []) })
      .catch(() => { if (alive) setPositions(null) })
    return () => { alive = false }
  }, [])

  const positionOpts = positionChoices(positions, form.position)

  // `positions === null` means the catalog request FAILED — including the window
  // where this ships before the Xano table exists. Falling back to the twelve
  // built-in names is right for CHOOSING, but offering to create one then would
  // just 404 after the confirm, so the create row is hidden until the catalog is
  // genuinely reachable.
  const canCreatePosition = !!isHrAdmin && Array.isArray(positions)

  // Combobox emits either an existing option or — with allowCreate — a brand-new
  // string. The new string is NOT written to the form here: a position is company
  // policy from the moment it exists, so it goes through a confirm and a POST first,
  // and the field only takes the value the server accepted.
  function onPositionChange(v) {
    const val = String(v || '').trim()
    if (!val) { set('position')(''); return }
    const known = positionOpts.find((p) => positionKey(p) === positionKey(val))
    if (known) { set('position')(known); return }
    if (canCreatePosition) setNewPosition(val)
  }

  async function confirmAddPosition() {
    // Collapsed the same way positionKey() collapses, so the string that reaches
    // the unique index is the one the duplicate check was run against.
    const name = newPosition.trim().replace(/\s+/g, ' ')
    if (!name) { setNewPosition(''); return }
    setAddingPosition(true)
    try {
      const row = await postJson('/api/hr/positions', { name })
      const saved = String(row?.name || name).trim()
      // The list page's ตำแหน่ง filter reads the same catalog through cachedGet,
      // which memoizes for the session — without this it would keep offering the
      // pre-add list until a reload, and the position just created would be the
      // one thing HR could not filter by.
      invalidateCache('/api/hr/positions')
      // Take the server's spelling, not the typed one — it trims, and if this
      // raced another HR account creating the same position we want their row.
      setPositions((prev) => {
        const list = Array.isArray(prev) ? prev : []
        return list.some((p) => positionKey(p?.name) === positionKey(saved)) ? list : [...list, row || { name: saved, active: true }]
      })
      set('position')(saved)
      setNewPosition('')
    } catch (e) {
      setErr(String(e?.message || e))
      setNewPosition('')
    } finally {
      setAddingPosition(false)
    }
  }

  async function reload() {
    if (!employeeId) return
    try {
      const e = await apiGet(`/api/hr/employees/${employeeId}`)
      if (e) { setSalary(Array.isArray(e.salary) ? e.salary : []); setRemark(Array.isArray(e.remark) ? e.remark : []); setImageUrl(e.image || '') }
    } catch { /* keep current */ }
  }

  function toggleRole(r) {
    setForm((f) => {
      const has = f.role.includes(r)
      return { ...f, role: has ? f.role.filter((x) => x !== r) : [...f.role, r] }
    })
  }

  function buildPayload() {
    const p = {
      prefix: form.prefix, name: form.name.trim(), nickname: form.nickname.trim(), name_eng: form.name_eng.trim(),
      email: form.email.trim(), phone: form.phone.trim(), address: form.address.trim(),
      idcardnumber: form.idcardnumber.trim(), code: derivedCode,
      position: form.position, role: form.role,
      branch_id: Number(form.branch_id) || 0, branch_acc_id: Number(form.branch_acc_id) || 0,
      bank: form.bank, bank_branchcode: form.bank_branchcode.trim(), bank_num: form.bank_num.trim(),
      // leavequota has no editor field any more — the การลา tab computes the
      // annual entitlement from date_probationpass (lib/leave.js), so the column
      // is dead. Kept in the payload so an existing value round-trips untouched.
      active: !!form.active, sso: !!form.sso, leavequota: Number(form.leavequota) || 0,
    }
    if (form.date_start) p.date_start = form.date_start
    if (form.date_probationpass) p.date_probationpass = form.date_probationpass
    if (form.date_end) p.date_end = form.date_end
    return p
  }

  function pickImage(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!/^image\//.test(file.type)) { setErr('ไฟล์ต้องเป็นรูปภาพ'); return }
    const preview = URL.createObjectURL(file)
    if (isEdit && employeeId) { uploadImage(employeeId, file) }
    else { setPendingImage({ file, preview }); setImageUrl(preview) }
  }

  async function uploadImage(id, file) {
    setUploading(true); setErr('')
    try {
      let out = null
      if (!streamBroken) {
        try { out = await postProfileImage(id, file, true) }
        catch (e) {
          if (e.refused) throw e
          // The streamed shape did not survive the hop to Xano. Not the user's
          // problem and not a reason to fail the upload — say it once, then take
          // the slow road. Same bargain as `directBroken` in lib/uploadDirect.js.
          streamBroken = true
          console.warn('[employee-image] streamed upload unavailable — falling back to the proxy:', e.message || e)
        }
      }
      if (!out) out = await postProfileImage(id, file, false)
      if (out?.url) setImageUrl(out.url)
    } catch (e) { setErr(String(e.message || e)) }
    finally { setUploading(false) }
  }

  async function save() {
    // Required wherever it is still editable — on create, and on an old row that has
    // no address yet. A locked field is never empty, so this never blocks a normal edit.
    if (!emailLocked && !form.email.trim()) { setErr('กรุณากรอกอีเมล'); return }
    if (!form.name.trim()) { setErr('กรุณากรอกชื่อ-นามสกุล'); return }
    if (form.phone.length !== PHONE_LEN) { setErr(`เบอร์โทรต้องเป็นตัวเลข ${PHONE_LEN} หลัก`); return }
    if (form.idcardnumber.length !== IDCARD_LEN) { setErr(`เลขบัตรประชาชนต้องเป็นตัวเลข ${IDCARD_LEN} หลัก`); return }
    if (!form.position) { setErr('กรุณาเลือกตำแหน่ง'); return }
    // branch 0 is the "— เลือกสาขา —" placeholder, not a real Branch id.
    if (!Number(form.branch_id)) { setErr('กรุณาเลือกสาขาที่ทำงาน'); return }
    if (!Number(form.branch_acc_id)) { setErr('กรุณาเลือกสาขาบัญชี/เงินเดือน'); return }
    if (!form.date_start) { setErr('กรุณาเลือกวันเริ่มงาน'); return }
    if (!form.role.length) { setErr('กรุณาเลือกบทบาทอย่างน้อย 1 รายการ'); return }
    // Turning someone off is an OFF-BOARDING, and payroll, the leave ledger and the
    // ประเมิน roster all read date_end to decide which months a person still counts
    // for. An inactive row with no end date is the one that silently keeps counting —
    // so the date is required here rather than left to be filled in "later".
    if (!form.active && !form.date_end) {
      setErr('ปิดใช้งานพนักงานต้องระบุ “วันสิ้นสุดการจ้าง” ก่อน'); return
    }
    // The three employment dates run in order. All are 'YYYY-MM-DD' from the date
    // inputs (toDateInput zero-pads), so comparing them as strings is safe.
    // วันสิ้นสุดการจ้าง is measured against วันผ่านทดลองงาน when there is one and
    // วันเริ่มงาน otherwise — an employee can be let go before probation ends.
    if (form.date_probationpass && form.date_probationpass < form.date_start) {
      setErr('วันผ่านทดลองงานต้องไม่ก่อนวันเริ่มงาน'); return
    }
    if (form.date_end) {
      const floor = form.date_probationpass || form.date_start
      if (form.date_end < floor) {
        setErr(form.date_probationpass
          ? 'วันสิ้นสุดการจ้างต้องไม่ก่อนวันผ่านทดลองงาน'
          : 'วันสิ้นสุดการจ้างต้องไม่ก่อนวันเริ่มงาน')
        return
      }
    }
    setSaving(true); setErr('')
    try {
      if (isEdit) {
        // Anything typed into the เงินเดือน / โน้ต rows counts as intent to save it —
        // the person filled it in and pressed บันทึก. Committed BEFORE the PATCH so a
        // rejected salary write stops the save with its own message on screen,
        // rather than closing the dialog over a line that never landed. Both are
        // no-ops when the row is empty, and both are edit-only (the sections do not
        // render on create — there is no employee to hang them off yet).
        if (canSalary) {
          const amt = Number(salaryDraft.amount)
          if (String(salaryDraft.amount).trim() !== '' && Number.isFinite(amt) && salaryDraft.name.trim()) {
            const ok = await salaryOp('new', undefined, {
              name: salaryDraft.name.trim(), amount: Math.round(amt), remark: salaryDraft.remark.trim(),
            })
            if (!ok) return
            setSalaryDraft((d) => ({ ...d, amount: '', remark: '' }))
          }
          if (noteDraft.trim()) {
            const ok = await remarkNew(noteDraft)
            if (!ok) return
            setNoteDraft('')
          }
        }
        await patchJson(`/api/hr/employees/${employeeId}`, buildPayload())
      } else {
        const created = await postJson('/api/hr/employees', buildPayload())
        const newId = created?.id
        if (newId && pendingImage?.file) await uploadImage(newId, pendingImage.file)
        // The account exists with no password until the new hire claims it, so a
        // claim email that did not send is the difference between "they can log in"
        // and "they cannot". Hold the dialog open on it and name the way back in.
        if (created?._claim_sent === false) {
          setCreatedId(Number(newId) || 0)
          setStoredEmail(String(created?.email || form.email || '').trim())
          // The server names the cause in Thai (_claim_error_th) — "ยังไม่มีอีเมล" and
          // "ส่งอีเมลไม่สำเร็จ" call for different reactions from HR.
          const why = String(created?._claim_error_th || '').trim()
          setClaimNote(
            `สร้างพนักงานเรียบร้อย แต่ส่งอีเมลเปิดใช้งานไม่สำเร็จ${why ? ` (${why})` : ''}` +
            ' — แจ้งพนักงานให้กด "ลืมรหัสผ่าน" ที่หน้าเข้าสู่ระบบ เพื่อตั้งรหัสผ่านด้วยตนเอง',
          )
          return
        }
      }
      onSaved?.()
    // The proxy answers a refused create with a Thai `{error}` (duplicate email /
    // duplicate account). api.js hands it over parsed on err.body — prefer it over
    // err.message, which is the raw "HTTP 400: {...}" envelope.
    } catch (e) { setErr(e?.body?.error || String(e.message || e)) }
    finally { setSaving(false) }
  }

  /* ── resend the "ตั้งรหัสผ่านครั้งแรก" mail ──
   * The address is NOT sent: /api/hr/employees/resend-invite reads it off the stored
   * Employee row, so what the confirm names is what actually gets mailed. A success
   * clears claimNote — that warning says the create's mail never went out, which
   * stops being true the moment one does. */
  async function resendInvite() {
    setAskingResend(false)
    setResending(true); setResendErr(''); setResendNote('')
    try {
      const r = await postJson('/api/hr/employees/resend-invite', { id: employeeId })
      setClaimNote('')
      setResendNote(`ส่งอีเมลตั้งรหัสผ่านไปที่ ${r?.email || storedEmail} แล้ว — ลิงก์หมดอายุใน 5 นาที`)
    } catch (e) {
      setResendErr(String(e?.body?.error || e.message || e))
    } finally { setResending(false) }
  }

  /* ── salary ops (HR/Admin, edit-only) ── */
  async function salaryOp(type, salary_old, salary_new) {
    try { await postJson('/api/hr/employee-salary', { employee_id: employeeId, type, salary_old, salary_new }); await reload(); return true }
    catch (e) { setErr(String(e.message || e)); return false }
  }
  /* ── note ops (HR/Admin, edit-only) ── */
  async function remarkNew(message) {
    if (!message.trim()) return true
    try { await postJson('/api/hr/employee-remark', { employee_id: employeeId, type: 'new', message: message.trim() }); await reload(); return true }
    catch (e) { setErr(String(e.message || e)); return false }
  }
  async function remarkDelete(entry) {
    try { await postJson('/api/hr/employee-remark', { employee_id: employeeId, type: 'delete', remark_old: entry }); await reload() }
    catch (e) { setErr(String(e.message || e)) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[640px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
          {/* The code, not a second "พนักงาน". This drawer now opens from every table
              in the section (EmployeeDrawer.jsx), so its first line has to answer WHO
              — and the code is the identity every one of those tables shows.
              form.code is the STORED code; derivedCode covers a row that has none yet
              and follows ชื่อเล่น + ชื่อจริง live while they are being typed. */}
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-[16px] font-semibold text-slate-900 shrink-0">{isEdit ? 'แก้ไขข้อมูล' : 'เพิ่มพนักงานใหม่'}</h2>
            {isEdit && !!(form.code || derivedCode) && (
              <span className="min-w-0 truncate rounded-lg bg-slate-100 text-slate-600 ring-1 ring-slate-200 px-2 py-0.5 text-[12.5px] font-medium tabular-nums">
                {form.code || derivedCode}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 size={20} className="animate-spin mr-2" /> กำลังโหลด…</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {/* profile image */}
            <div className="flex items-center gap-4 mb-2">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 text-slate-300 flex items-center justify-center overflow-hidden ring-1 ring-slate-200 shrink-0">
                {imageUrl ? <img src={imageUrl} alt="" className="w-full h-full object-cover" /> : <UserRound size={26} />}
              </div>
              <div>
                <button
                  type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-slate-600 ring-1 ring-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
                >
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} เปลี่ยนรูปโปรไฟล์
                </button>
                {!isEdit && <p className="text-[11px] text-slate-400 mt-1">รูปจะอัปโหลดหลังบันทึกพนักงาน</p>}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
              </div>
            </div>

            {/* personal */}
            <SectionHead icon={UserRound} title="ข้อมูลส่วนตัว" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="คำนำหน้า">
                <SearchableSelect options={PREFIX_OPTS} value={form.prefix} onChange={set('prefix')} placeholder="— เลือกคำนำหน้า —" />
              </Field>
              <Field label="ชื่อเล่น"><input value={form.nickname} onChange={set('nickname')} className={FIELD} /></Field>
              <Field label="ชื่อ-นามสกุล" required><input value={form.name} onChange={set('name')} className={FIELD} /></Field>
              <Field label="ชื่อ (อังกฤษ)"><input value={form.name_eng} onChange={set('name_eng')} className={FIELD} /></Field>
              <Field label="เบอร์โทร" required aside={<Counter value={form.phone} len={PHONE_LEN} />}>
                <input value={form.phone} onChange={setDigits('phone', PHONE_LEN)} className={FIELD}
                  inputMode="numeric" autoComplete="off" placeholder="0812345678" />
              </Field>
              {/* Locked after creation. The address is the LOGIN identity: POST
                  /employee/new creates the matching `user` row from it, and editing
                  Employee.email here would not touch that row — the person would keep
                  signing in with the old address while HR reads the new one, and the
                  duplicate check that guards creation never runs again. Changing it
                  for real means changing both rows, which is a Xano operation, not a
                  field in this dialog. The PATCH proxy drops `email` too, so this is
                  not a hidden-input-away lock. */}
              {/* No "แก้ที่นี่ไม่ได้" hint: the padlock and the greyed field already say it,
                  and the line landed UNDER the ส่งอีเมล button (Field renders hint last),
                  reading as a caption for the wrong control. */}
              <Field label="อีเมล" required={!emailLocked}>
                {emailLocked
                  ? <LockedInput value={form.email} type="email" />
                  : <input value={form.email} onChange={set('email')} className={FIELD} type="email" />}
                {/* Only ever the "the activation mail did not send" warning. A new
                    account is created with NO password and is claimed through the
                    emailed link; if that mail fails, the employee reaches the same
                    place through ลืมรหัสผ่าน on the login page. */}
                {claimNote && (
                  <p className="text-[11.5px] mt-1.5 leading-snug text-amber-600">{claimNote}</p>
                )}
                {/* ส่งอีเมลตั้งรหัสผ่านอีกครั้ง — edit mode, and only once an address is
                    stored (a row without one has no `user` account to claim). Shown
                    always rather than only after a failed create: the link expires in
                    five minutes, so "the mail sent fine" and "the hire can still use
                    it" are different facts and only the first one is knowable here. */}
                {isEdit && !!storedEmail && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setAskingResend(true)}
                      disabled={resending || !employeeId}
                      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-semibold text-brand-700 ring-1 ring-brand-200 bg-brand-50 hover:bg-brand-100 disabled:opacity-40"
                    >
                      {resending ? <Loader2 size={12} className="animate-spin" /> : <MailCheck size={12} />}
                      ส่งอีเมลตั้งรหัสผ่านอีกครั้ง
                    </button>
                    {resendNote && <p className="text-[11.5px] mt-1.5 leading-snug text-emerald-600">{resendNote}</p>}
                    {resendErr && <p className="text-[11.5px] mt-1.5 leading-snug text-rose-600">{resendErr}</p>}
                  </div>
                )}
              </Field>
              <Field label="เลขบัตรประชาชน" required aside={<Counter value={form.idcardnumber} len={IDCARD_LEN} />}>
                <input value={form.idcardnumber} onChange={setDigits('idcardnumber', IDCARD_LEN)} className={FIELD}
                  inputMode="numeric" autoComplete="off" placeholder="1234567890123" />
              </Field>
              {/* Read-only on purpose: the code IS ชื่อเล่น + ชื่อจริง, and every
                  screen that names a person reads it. Typing it by hand is how it
                  used to drift away from the name above it. */}
              <Field
                label="รหัสพนักงาน"
                hint={isEdit && form.code && form.code !== derivedCode
                  ? `เดิม: ${form.code} — บันทึกแล้วจะเปลี่ยนเป็นด้านบน`
                  : 'สร้างอัตโนมัติจาก ชื่อเล่น + ชื่อจริง'}
              >
                <LockedInput value={derivedCode} placeholder="— กรอกชื่อเล่นและชื่อ —" />
              </Field>
              <div className="col-span-2">
                <Field label="ที่อยู่"><textarea value={form.address} onChange={set('address')} rows={2} className={FIELD} /></Field>
              </div>
            </div>

            {/* employment */}
            <SectionHead icon={UserRound} title="การจ้างงาน" />
            <div className="grid grid-cols-2 gap-3">
              {/* Combobox rather than the app's SearchableSelect, for the create row:
                  ตำแหน่ง is an HR-managed catalog, so HR/Admin may type a name that is
                  not in it yet and add it from here. `allowCreate` is off for everyone
                  else, which leaves the control a plain picker for a Manager. */}
              <Field label="ตำแหน่ง" required>
                <Combobox
                  value={form.position}
                  onChange={onPositionChange}
                  options={positionOpts}
                  allowCreate={canCreatePosition}
                  createLabel={(q) => `＋ เพิ่มตำแหน่ง “${q}” เข้าระบบ`}
                  placeholder="— เลือกตำแหน่ง —"
                  white
                />
              </Field>
              <Field label="สถานะ">
                <Toggle checked={form.active} onChange={set('active')} labelOn="ใช้งาน (เข้าระบบได้)" labelOff="ปิดใช้งาน (ออกจากระบบ)" />
                {/* Said HERE, next to the switch that caused it, and not only as the
                    red line บันทึก throws — by then the person has filled the whole
                    form and is being refused for a field two rows down. */}
                {!form.active && !form.date_end && (
                  <p className="text-[11.5px] text-amber-600 mt-0.5 leading-snug">
                    ต้องระบุ “วันสิ้นสุดการจ้าง” ด้านล่างก่อน จึงจะปิดใช้งานได้
                  </p>
                )}
              </Field>
              <Field label="สาขาที่ทำงาน" required>
                <SearchableSelect options={branchOptsFor(form.branch_id)} value={Number(form.branch_id)} onChange={(v) => set('branch_id')(Number(v))} placeholder="— เลือกสาขา —" />
              </Field>
              <Field label="สาขาบัญชี/เงินเดือน" required>
                <SearchableSelect options={branchOptsFor(form.branch_acc_id)} value={Number(form.branch_acc_id)} onChange={(v) => set('branch_acc_id')(Number(v))} placeholder="— เลือกสาขา —" />
              </Field>
              <Field label="วันเริ่มงาน" required><input type="date" value={form.date_start} onChange={set('date_start')} className={FIELD} /></Field>
              <Field label="วันผ่านทดลองงาน"><input type="date" value={form.date_probationpass} onChange={set('date_probationpass')} className={FIELD} /></Field>
              {/* Required only while ปิดใช้งาน is set — an active employee has no end
                  date yet, and demanding one would be asking HR to invent it. */}
              <Field label="วันสิ้นสุดการจ้าง" required={!form.active}>
                <input type="date" value={form.date_end} onChange={set('date_end')}
                  className={!form.active && !form.date_end ? FIELD_WARN : FIELD} />
              </Field>
              <div className="col-span-2">
                <label className={LABEL}>บทบาท (Role)<span className="text-rose-500"> *</span></label>
                <div className="flex flex-wrap gap-1.5">
                  {/* Admin and Doctor are not assignable here — see LOCKED_ROLES. An
                      account that already holds one still shows the chip, locked, so the
                      fact is never hidden; it just cannot be granted or dropped from this
                      dialog. `form.role` carries it untouched, so saving preserves it. */}
                  {LOCKED_ROLES.filter((r) => form.role.includes(r)).map((r) => (
                    <span
                      key={r}
                      title={LOCKED_ROLE_HINT[r]}
                      className="px-2.5 py-1 rounded-lg text-[12.5px] font-medium ring-1 bg-slate-100 text-slate-500 ring-slate-200 cursor-not-allowed inline-flex items-center gap-1"
                    >
                      <Lock size={11} strokeWidth={2} /> {r}
                    </span>
                  ))}
                  {ASSIGNABLE_ROLES.map((r) => {
                    const on = form.role.includes(r)
                    return (
                      <button key={r} type="button" onClick={() => toggleRole(r)}
                        className={`px-2.5 py-1 rounded-lg text-[12.5px] font-medium ring-1 transition ${on ? 'bg-brand-50 text-brand-700 ring-brand-200' : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                        {r}
                      </button>
                    )
                  })}
                </div>
              </div>
              <Field label="ประกันสังคม (สปส.)">
                <Toggle checked={form.sso} onChange={set('sso')} labelOn="ส่ง สปส." labelOff="ไม่ส่ง สปส." />
              </Field>
            </div>

            {/* bank */}
            <SectionHead icon={BadgeDollarSign} title="บัญชีธนาคาร" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="ธนาคาร">
                <SearchableSelect options={bankOptions(form.bank)} value={form.bank} onChange={set('bank')} placeholder="— เลือกธนาคาร —" />
              </Field>
              <Field label="รหัสสาขา">
                <input value={form.bank_branchcode} onChange={setDigits('bank_branchcode')} className={FIELD} inputMode="numeric" autoComplete="off" />
              </Field>
              <Field label="เลขที่บัญชี">
                <input value={form.bank_num} onChange={setDigits('bank_num')} className={FIELD} inputMode="numeric" autoComplete="off" />
              </Field>
            </div>

            {/* salary + note — HR/Admin only, edit-only */}
            {canSalary && (isEdit ? (
              <>
                <SectionHead icon={BadgeDollarSign} title="เงินเดือน" note="เฉพาะ HR/Admin" />
                <SalaryEditor entries={salary} draft={salaryDraft} onDraft={setSalaryDraft} onOp={salaryOp} />
                <SectionHead icon={StickyNote} title="โน้ต" note="เฉพาะ HR/Admin" />
                <NoteEditor entries={remark} draft={noteDraft} onDraft={setNoteDraft} onAdd={remarkNew} onDelete={remarkDelete} />
              </>
            ) : (
              <p className="mt-6 text-[12.5px] text-slate-400">บันทึกพนักงานก่อน จึงจะเพิ่มเงินเดือนและโน้ตได้</p>
            ))}

            {/* contracts — edit-only: a contract is issued TO somebody, so there is
                nothing to offer until the employee record exists. Same rule as the
                salary/note sections above. Generating does NOT save anything: it
                fills a Google Docs template and hands back a PDF, so the record is
                unchanged whether or not a contract is ever signed. */}
            {isEdit && (
              <>
                <SectionHead icon={FileText} title="เอกสารสัญญา" />
                <button
                  type="button" onClick={() => setContractOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-200 hover:bg-brand-100">
                  <FileText size={14} /> ออกสัญญาจ้างงาน / สัญญาฝึกอบรม
                </button>
                <p className="text-[11.5px] text-slate-400 mt-1.5">สร้างไฟล์ PDF จากข้อมูลพนักงาน — ตรวจทานทุกช่องก่อนสร้าง</p>
              </>
            )}
          </div>
        )}

        {/* footer */}
        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
          <div className="flex items-center gap-2 shrink-0">
            {/* Once a create has gone through, บันทึก must not stay on screen — pressing
                it again would create a SECOND employee. The only way out is the one
                that also refreshes the list behind the dialog. */}
            {createdId ? (
              <button onClick={() => onSaved?.()} className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
                เสร็จสิ้น
              </button>
            ) : (
              <>
                <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
                <button onClick={save} disabled={saving || loading} className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                  {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Seeded from `form` + `salary`, i.e. what is on screen — not from the last
          saved record. HR routinely fixes an address or a salary and issues the
          contract in the same sitting, and a contract that disagreed with the
          form the user is looking at would be the surprising behaviour. */}
      {contractOpen && (
        <ContractDialog
          employee={{ ...form, id: employeeId, idcardnumber: form.idcardnumber, salary }}
          branches={branches}
          onClose={() => setContractOpen(false)}
        />
      )}

      {/* Adding a position is not an edit to this employee — it changes the dropdown
          every HR account sees from now on, and a Thai near-duplicate (เเ for แ) would
          read as the same word while counting as a different job on every roster and
          evaluation rule. Hence a confirm that shows the exact string being created. */}
      {newPosition && (
        <ConfirmDialog
          title="เพิ่มตำแหน่งใหม่"
          message={`เพิ่ม “${newPosition}” เป็นตำแหน่งพนักงานในระบบ — จะขึ้นในรายการให้ทุกคนเลือกได้ ตรวจตัวสะกดให้ถูกต้องก่อนยืนยัน${addingPosition ? '\n\nกำลังบันทึก…' : ''}`}
          confirmLabel="เพิ่มตำแหน่ง"
          onConfirm={confirmAddPosition}
          onCancel={() => setNewPosition('')}
        />
      )}

      {/* Naming the address is the point of asking: the endpoint mails whatever is
          STORED, so this is HR's one chance to notice it is not the address they
          think it is. */}
      {askingResend && (
        <ConfirmDialog
          title="ส่งอีเมลตั้งรหัสผ่านอีกครั้ง?"
          message={`ระบบจะส่งลิงก์ “ตั้งรหัสผ่านครั้งแรก” ไปที่ ${storedEmail} — ลิงก์ใช้ได้ครั้งเดียวและหมดอายุใน 5 นาที`}
          confirmLabel="ส่งอีเมล"
          onConfirm={resendInvite}
          onCancel={() => setAskingResend(false)}
        />
      )}
    </div>
  )
}

/* ── salary[] inline editor ─────────────────────────────────────────────── */
// The half-typed row lives in the PARENT (`draft`), not here, so that บันทึก can
// commit it as well as ＋ — see the comment on salaryDraft in EmployeeEditor.
function SalaryEditor({ entries, draft, onDraft, onOp }) {
  const { name, amount, remark } = draft
  const setName = (v) => onDraft((d) => ({ ...d, name: v }))
  const setAmount = (v) => onDraft((d) => ({ ...d, amount: v }))
  const setRemark = (v) => onDraft((d) => ({ ...d, remark: v }))
  const add = () => {
    const amt = Number(amount)
    if (!name.trim() || String(amount).trim() === '' || !Number.isFinite(amt)) return
    onOp('new', undefined, { name: name.trim(), amount: Math.round(amt), remark: remark.trim() })
    onDraft((d) => ({ ...d, amount: '', remark: '' }))
  }
  return (
    <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
      {entries.length === 0 && <div className="px-3 py-2.5 text-[12.5px] text-slate-400">ยังไม่มีรายการเงินเดือน</div>}
      {entries.map((s, i) => (
        <div key={i} className="px-3 py-2.5 flex items-center gap-2 text-[13px]">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-700 truncate">{s.name}</div>
            {s.remark && <div className="text-[11.5px] text-slate-400 truncate">{s.remark}</div>}
          </div>
          <div className="tabular-nums text-slate-700">{Number(s.amount || 0).toLocaleString()}</div>
          <button onClick={() => onOp('delete', s)} className="p-1 rounded text-slate-300 hover:text-rose-500"><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="px-3 py-2.5 flex items-center gap-2">
        {/* Combobox, not <datalist> — a datalist is an OS-drawn dropdown, and the
            two known names still have to be free-text (a one-off line is legal). */}
        <div className="w-[38%]">
          <Combobox options={SALARY_NAMES} value={name} onChange={setName} placeholder="รายการ" freeText allowCreate dense white />
        </div>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="จำนวน" className="w-[26%] rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] outline-none focus:border-brand-400" />
        <input value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="หมายเหตุ" className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] outline-none focus:border-brand-400" />
        <button onClick={add} className="p-1.5 rounded-lg text-white bg-brand-600 hover:bg-brand-700"><Plus size={14} /></button>
      </div>
    </div>
  )
}

/* ── remark[] (notes) ───────────────────────────────────────────────────── */
// `draft` is the parent's, for the same reason SalaryEditor's is: an unsent note
// left in the box must go in when บันทึก is pressed, not be thrown away.
function NoteEditor({ entries, draft, onDraft, onAdd, onDelete }) {
  const msg = draft
  const send = () => { if (msg.trim()) { onAdd(msg); onDraft('') } }
  const sorted = [...entries].sort((a, b) => Number(b?.timestamp || 0) - Number(a?.timestamp || 0))
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <input value={msg} onChange={(e) => onDraft(e.target.value)} placeholder="เพิ่มโน้ต…" className={FIELD}
          onKeyDown={(e) => { if (e.key === 'Enter') send() }} />
        <button onClick={send} className="px-3 py-2 rounded-xl text-white bg-brand-600 hover:bg-brand-700 shrink-0"><Plus size={15} /></button>
      </div>
      <div className="space-y-1.5">
        {sorted.length === 0 && <p className="text-[12.5px] text-slate-400">ยังไม่มีโน้ต</p>}
        {sorted.map((r, i) => (
          <div key={i} className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{r.message}</div>
              {r.timestamp && <div className="text-[11px] text-slate-400 mt-0.5">{fmtDateTH(Number(r.timestamp))}</div>}
            </div>
            <button onClick={() => onDelete(r)} className="p-1 rounded text-slate-300 hover:text-rose-500 shrink-0"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  )
}
